import { useCallback, useState } from 'react';
import { formatDateTime } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { useSyncLogs } from '@/hooks/useSyncLogs';
import { usePagination } from '@/hooks/usePagination';
import type { SyncEvent } from '@/types';

const columns: DataTableColumn<SyncEvent>[] = [
  {
    key: 'direction',
    label: 'Direction',
    className: 'w-[80px]',
    render: (item) => (
      <Badge
        variant={item.direction === 'inbound' ? 'default' : 'secondary'}
        className="text-[10px] w-14 justify-center"
      >
        {item.direction === 'inbound' ? 'IN' : 'OUT'}
      </Badge>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    className: 'w-[80px]',
    render: (item) => (
      <Badge
        variant={
          item.status === 'success'
            ? 'success'
            : item.status === 'failed'
              ? 'destructive'
              : 'warning'
        }
        className="text-[10px] w-14 justify-center"
      >
        {item.status}
      </Badge>
    ),
  },
  {
    key: 'event_category',
    label: 'Category',
    className: 'w-[100px]',
    render: (item) => (
      <span className="text-xs text-muted-foreground capitalize">{item.event_category}</span>
    ),
  },
  {
    key: 'title',
    label: 'Event',
    render: (item) => (
      <div>
        <p className="text-sm text-foreground">{item.title}</p>
        {item.description && (
          <p className="text-xs text-muted-foreground truncate max-w-md">{item.description}</p>
        )}
      </div>
    ),
  },
  {
    key: 'source',
    label: 'Source',
    className: 'w-[100px]',
    render: (item) => (
      <span className="text-xs text-muted-foreground capitalize">{item.source ?? '\u2014'}</span>
    ),
  },
  {
    key: 'created_at',
    label: 'Time',
    sortable: true,
    className: 'w-[140px]',
    render: (item) => (
      <span className="text-xs text-muted-foreground">{formatDateTime(item.created_at)}</span>
    ),
  },
];

function SyncMobileCard({ item }: { item: SyncEvent }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge
          variant={item.direction === 'inbound' ? 'default' : 'secondary'}
          className="text-[10px] w-14 justify-center"
        >
          {item.direction === 'inbound' ? 'IN' : 'OUT'}
        </Badge>
        <Badge
          variant={
            item.status === 'success'
              ? 'success'
              : item.status === 'failed'
                ? 'destructive'
                : 'warning'
          }
          className="text-[10px] w-14 justify-center"
        >
          {item.status}
        </Badge>
        <span className="text-xs capitalize text-muted-foreground">
          {item.event_category}
        </span>
      </div>

      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{item.title}</p>
        {item.description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {item.description}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="capitalize">{item.source ?? 'No source'}</span>
        <span>{formatDateTime(item.created_at)}</span>
      </div>
    </div>
  );
}

export function SyncHistoryPage() {
  const { page, perPage, search, sort, setPage, setSearch, setSort } = usePagination({
    initialSort: { field: 'created_at', direction: 'desc' },
  });

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');

  const { data, isLoading, isFetching } = useSyncLogs({
    page,
    per_page: perPage,
    search,
    sort_by: sort.field,
    sort_dir: sort.direction,
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    direction: directionFilter !== 'all' ? directionFilter : undefined,
  });

  const handleCategoryFilter = useCallback(
    (value: string) => {
      setCategoryFilter(value);
      setPage(1);
    },
    [setPage],
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setStatusFilter(value);
      setPage(1);
    },
    [setPage],
  );

  const handleDirectionFilter = useCallback(
    (value: string) => {
      setDirectionFilter(value);
      setPage(1);
    },
    [setPage],
  );

  const filterBar = (
    <>
      <Select value={categoryFilter} onValueChange={handleCategoryFilter}>
        <SelectTrigger className="text-base sm:text-sm md:h-8 md:w-[120px] md:text-xs">
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All categories</SelectItem>
          <SelectItem value="products">Products</SelectItem>
          <SelectItem value="inventory">Inventory</SelectItem>
          <SelectItem value="orders">Orders</SelectItem>
          <SelectItem value="customers">Customers</SelectItem>
          <SelectItem value="system">System</SelectItem>
        </SelectContent>
      </Select>

      <Select value={statusFilter} onValueChange={handleStatusFilter}>
        <SelectTrigger className="text-base sm:text-sm md:h-8 md:w-[110px] md:text-xs">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="success">Success</SelectItem>
          <SelectItem value="failed">Failed</SelectItem>
          <SelectItem value="skipped">Skipped</SelectItem>
        </SelectContent>
      </Select>

      <Select value={directionFilter} onValueChange={handleDirectionFilter}>
        <SelectTrigger className="text-base sm:text-sm md:h-8 md:w-[110px] md:text-xs">
          <SelectValue placeholder="Direction" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All directions</SelectItem>
          <SelectItem value="inbound">Inbound</SelectItem>
          <SelectItem value="outbound">Outbound</SelectItem>
        </SelectContent>
      </Select>

      {(categoryFilter !== 'all' || statusFilter !== 'all' || directionFilter !== 'all') && (
        <Button
          variant="ghost"
          size="sm"
          className="h-10 justify-center text-xs md:h-8"
          onClick={() => {
            setCategoryFilter('all');
            setStatusFilter('all');
            setDirectionFilter('all');
            setPage(1);
          }}
        >
          Clear
        </Button>
      )}
    </>
  );

  return (
    <div className="h-full flex flex-col">
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        total={data?.total ?? 0}
        page={page}
        perPage={perPage}
        sort={sort}
        search={search}
        isLoading={isLoading}
        isFetching={isFetching}
        getRowId={(item) => item.id}
        onPageChange={setPage}
        onSortChange={setSort}
        onSearchChange={setSearch}
        searchPlaceholder="Search events..."
        emptyMessage="No sync events found"
        filterBar={filterBar}
        mobileRender={(item) => <SyncMobileCard item={item} />}
      />
    </div>
  );
}
